import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { cn } from '@tsgecart/ui';
import { useCategories } from '../../catalog/hooks/use-catalog.js';
import { StorefrontSkeletonGrid, StorefrontErrorState } from '../../components/states.js';
import type { Category } from '../../catalog/types/catalog.js';

const HEADING_CLASS = 'text-xl font-bold tracking-tight text-gray-900 sm:text-2xl';

function CategoryIcon({ category }: { category: Category }) {
  const [failed, setFailed] = useState(false);
  if (category.imageUrl && !failed) {
    return (
      <img
        src={category.imageUrl}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        className="h-12 w-12 rounded-full object-cover transition-transform duration-300 group-hover:scale-110"
      />
    );
  }
  return (
    <span className="bg-brand/20 flex h-12 w-12 items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110">
      <LayoutGrid className="text-brand-foreground h-5 w-5" aria-hidden="true" />
    </span>
  );
}

/** Top-level categories as clickable cards. Fails independently of the rest of the homepage -- see `ProductSection` for the same pattern. */
export function FeaturedCategoriesSection() {
  const { data, isLoading, isError, refetch } = useCategories();
  const topLevel = (data ?? [])
    .filter((category) => category.parentId === null)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  if (isLoading) {
    return (
      <section className="py-10 sm:py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <h2 className={cn('mb-5', HEADING_CLASS)}>Shop by category</h2>
          <StorefrontSkeletonGrid count={6} className="h-28" />
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="py-10 sm:py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <h2 className={cn('mb-5', HEADING_CLASS)}>Shop by category</h2>
          <StorefrontErrorState message="Couldn't load categories." onRetry={() => void refetch()} />
        </div>
      </section>
    );
  }

  if (topLevel.length === 0) return null;

  return (
    <section className="py-10 sm:py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <h2 className={cn('mb-5', HEADING_CLASS)}>Shop by category</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {topLevel.map((category) => (
            <Link
              key={category.id}
              to={`${ROUTE_NAMES.category}/${category.slug}`}
              className="focus-visible:ring-brand group flex flex-col items-center gap-2 rounded-2xl border border-gray-100 bg-white p-4 text-center transition duration-300 hover:-translate-y-1 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2"
            >
              <CategoryIcon category={category} />
              <span className="text-sm font-medium text-gray-900">{category.name}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
